/**
 * @desc Refference list of eip155 chains
 * @url https://chainlist.org
 */

import { Network } from "./types";

function getNetworkById(chains: Record<string, Network>) {
  return Object.entries(chains).reduce<Record<Network["chainId"], string>>(
    (acc, [key, network]) => {
      acc[network.chainId] = key;
      return acc;
    },
    {},
  );
}

export const EIP155_CHAINS_MAINNET: Record<string, Network> = {
  ethereum: {
    chainId: 1,
    namespace: "eip155:1",
    ticker: "ETH",
    displayName: "Ethereum",
    color: "#0ebdcd",
  },
  bsc: {
    chainId: 56,
    namespace: "eip155:56",
    ticker: "BNB",
    displayName: "Binance Smart Chain",
    color: "#F0B90A",
  },
  polygon: {
    chainId: 137,
    namespace: "eip155:137",
    ticker: "MATIC",
    displayName: "Polygon",
    color: "#6d29de",
  },
  rsk: {
    chainId: 30,
    namespace: "eip155:30",
    ticker: "RBTC",
    displayName: "Rootstock",
    color: "#ff9100",
  },
  optimism: {
    chainId: 10,
    namespace: "eip155:10",
    ticker: "ETH",
    displayName: "Optimism",
    color: "#FF0421",
  },
  arbitrum: {
    chainId: 42161,
    namespace: "eip155:42161",
    ticker: "ETH",
    displayName: "Arbitrum",
    color: "#28a0f0",
  },
  avalanche_c_chain: {
    chainId: 43114,
    namespace: "eip155:43114",
    ticker: "AVAX",
    displayName: "Avalanche C-Chain",
    color: "#E84142",
  },
  base: {
    chainId: 8453,
    namespace: "eip155:8453",
    ticker: "ETH",
    displayName: "Base",
    color: "#1755FE",
  },
  core: {
    chainId: 1116,
    namespace: "eip155:1116",
    ticker: "CORE",
    displayName: "Core",
    color: "#FF962B",
  },
  monad: {
    chainId: 143,
    namespace: "eip155:143",
    ticker: "MON",
    displayName: "Monad",
    color: "#836EF9",
  },
  adi: {
    chainId: 36900,
    namespace: "eip155:36900",
    ticker: "ADI",
    displayName: "ADI Chain",
    color: "#1E72B9",
  },
  sei_evm: {
    chainId: 1329,
    namespace: "eip155:1329",
    ticker: "SEI",
    displayName: "Sei EVM",
    color: "#7B2CBF",
  },
  somnia: {
    chainId: 5031,
    namespace: "eip155:5031",
    ticker: "SOMI",
    displayName: "Somnia",
    color: "#6F0191",
  },
  etherlink: {
    chainId: 42793,
    namespace: "eip155:42793",
    ticker: "XTZ",
    displayName: "Etherlink",
    color: "#38FF9C",
  },
  zero_gravity: {
    chainId: 16661,
    namespace: "eip155:16661",
    ticker: "0G",
    displayName: "0G",
    color: "#9200E1",
  },
  robinhood: {
    chainId: 4663,
    namespace: "eip155:4663",
    ticker: "ETH",
    displayName: "Robinhood Chain",
    color: "#00C805",
  },
  arc: {
    chainId: 5042,
    namespace: "eip155:5042",
    ticker: "USDC",
    displayName: "Arc",
    color: "#1A2B5F",
  },
};

export const EIP155_SEPOLIA_CHAINS: Record<string, Network> = {
  ethereum_sepolia: {
    chainId: 11155111,
    namespace: "eip155:11155111",
    ticker: "ETH",
    displayName: "Ethereum Sepolia",
    color: "#00ff00",
  },
  arbitrum_sepolia: {
    chainId: 421614,
    namespace: "eip155:421614",
    ticker: "ETH",
    displayName: "Arbitrum Sepolia",
    color: "#28a0f0",
  },
  optimism_sepolia: {
    chainId: 11155420,
    namespace: "eip155:11155420",
    ticker: "ETH",
    displayName: "Optimism Sepolia",
    color: "#00ff00",
  },
  base_sepolia: {
    chainId: 84531,
    namespace: "eip155:84531",
    ticker: "ETH",
    displayName: "Base Sepolia",
    color: "#FF0052",
  },
};

export const EIP155_HOLESKY_CHAINS: Record<string, Network> = {
  ethereum_holesky: {
    chainId: 17000,
    namespace: "eip155:17000",
    ticker: "ETH",
    displayName: "Ethereum Holesky",
    color: "#00ff00",
  },
};

export const EIP155_CHAINS_TESTNET: Record<string, Network> = {
  monad_testnet: {
    chainId: 10143,
    namespace: "eip155:10143",
    ticker: "MON",
    displayName: "Monad Testnet",
    color: "#836EF9",
  },
  arc_testnet: {
    chainId: 5042002,
    namespace: "eip155:5042002",
    ticker: "USDC",
    displayName: "Arc Testnet",
    color: "#6B7280",
  },
};

export const BIP122_MAINNET_CHAINS: Record<string, Network> = {
  bitcoin: {
    chainId: "000000000019d6689c085ae165831e93",
    namespace: "bip122:000000000019d6689c085ae165831e93",
    displayName: "Bitcoin",
    ticker: "BTC",
    color: "#F7931A",
  },
  litecoin: {
    chainId: "12a765e31ffd4059bada1e25190f6e98",
    namespace: "bip122:12a765e31ffd4059bada1e25190f6e98",
    displayName: "Litecoin",
    ticker: "LTC",
    color: "#355DAF",
  },
  dogecoin: {
    chainId: "82bc68038f6034c0596b6e313729793a",
    namespace: "bip122:82bc68038f6034c0596b6e313729793a",
    displayName: "Dogecoin",
    ticker: "DOGE",
    color: "#65d196",
  },
};

export const BIP122_TEST_CHAINS: Record<string, Network> = {
  bitcoin_testnet: {
    chainId: "000000000933ea01ad0ee984209779ba",
    namespace: "bip122:000000000933ea01ad0ee984209779ba",
    displayName: "Bitcoin (Testnet)",
    ticker: "TBTC",
    color: "#F7931A",
  },
};

export const BIP122_CHAINS = {
  ...BIP122_MAINNET_CHAINS,
  ...BIP122_TEST_CHAINS,
};

export const BIP122_NETWORK_BY_CHAIN_ID = getNetworkById(BIP122_CHAINS);

export const RIPPLE_CHAINS: Record<string, Network> = {
  ripple: {
    chainId: "0",
    namespace: "xrpl:0",
    ticker: "XRP",
    displayName: "XRPL",
    color: "#23F7DD",
  },
};

export const RIPPLE_NETWORK_BY_CHAIN_ID = getNetworkById(RIPPLE_CHAINS);

export const SOLANA_MAINNET_CHAINS: Record<string, Network> = {
  solana: {
    chainId: "1",
    namespace: "solana:5eykt4UsFv8P8NJdTREpY1vzqKqZKvdp",
    // ^ seem to only work for https://react-app.walletconnect.com/
    // see https://github.com/WalletConnect/web-examples/blob/a172c268d4f3505aa6c251a70ddd7bae5676414b/advanced/wallets/react-wallet-v2/src/data/SolanaData.ts#L11
    ticker: "SOL",
    displayName: "Solana",
    color: "#1EF0A6",
  },
  // LEGACY, BUT SEEM WIDESPREAD IN ITS USAGE...
  "solana (legacy)": {
    chainId: "1",
    namespace: "solana:4sGjMW1sUnHzSxGspuhpqLDx6wiyjNtZ", // work with APPs (legacy)
    ticker: "SOL",
    displayName: "Solana",
    color: "#1EF0A6",
  },
};

export const SOLANA_TEST_CHAINS: Record<string, Network> = {
  solana_testnet: {
    chainId: "4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z",
    namespace: "solana:4uhcVJyU9pJkvQyS88uRDiswHXSCkY3z", // work with APPs (legacy)
    ticker: "SOL",
    displayName: "Solana testnet",
    color: "#1EF0A6",
  },
  solana_devnet: {
    chainId: "EtWTRABZaYq6iMfeYKouRu166VU2xqa1",
    namespace: "solana:EtWTRABZaYq6iMfeYKouRu166VU2xqa1", // work with APPs (legacy)
    ticker: "SOL",
    displayName: "Solana devnet",
    color: "#1EF0A6",
  },
};

export const SOLANA_CHAINS = {
  ...SOLANA_MAINNET_CHAINS,
  ...SOLANA_TEST_CHAINS,
};

export const SOLANA_NETWORK_BY_CHAIN_ID = getNetworkById(SOLANA_CHAINS);

export const TEZOS_CHAINS: Record<string, Network> = {
  tezos: {
    chainId: "NetXdQprcVkpaWU",
    namespace: "tezos:NetXdQprcVkpaWU",
    ticker: "XTZ",
    displayName: "Tezos",
    color: "#2C7DF7",
  },
  // dApps send either the canonical id or Taquito's `tezos:mainnet` alias; register both (D2).
  "tezos (taquito)": {
    chainId: "NetXdQprcVkpaWU",
    namespace: "tezos:mainnet",
    ticker: "XTZ",
    displayName: "Tezos",
    color: "#2C7DF7",
  },
};

export const COSMOS_CHAINS: Record<string, Network> = {
  babylon: {
    chainId: "bbn-1",
    namespace: "cosmos:bbn-1",
    ticker: "BABY",
    displayName: "Babylon",
    color: "#CE6533",
  },
};

export const EIP155_CHAINS = {
  ...EIP155_CHAINS_MAINNET,
  ...EIP155_SEPOLIA_CHAINS,
  ...EIP155_HOLESKY_CHAINS,
  ...EIP155_CHAINS_TESTNET,
};

export const EIP155_NETWORK_BY_CHAIN_ID = getNetworkById(EIP155_CHAINS);

export const SUPPORTED_NETWORK: Record<string, Network> = {
  ...EIP155_CHAINS,
  ...BIP122_CHAINS,
  ...RIPPLE_CHAINS,
  ...SOLANA_CHAINS,
  ...TEZOS_CHAINS,
  ...COSMOS_CHAINS,
};

/**
 * Networks named in the "we currently support" copy of the unsupported-chain
 * screen. Advertising is opt-in per map: testnets stay out, and Babylon stays
 * out while LIVE-27227 is On Hold even though its cosmos handlers are wired.
 * A new mainnet map has to be added here or it will never be advertised.
 */
const ADVERTISED_NETWORKS: Record<string, Network> = {
  ...EIP155_CHAINS_MAINNET,
  ...BIP122_MAINNET_CHAINS,
  ...RIPPLE_CHAINS,
  ...SOLANA_MAINNET_CHAINS,
  ...TEZOS_CHAINS,
};

// Alias entries ("solana (legacy)", "tezos (taquito)") reuse a canonical display name.
export const SUPPORTED_NETWORK_NAMES: string[] = [
  ...new Set(
    Object.values(ADVERTISED_NETWORKS).map(
      (network: Network) => network.displayName,
    ),
  ),
];

export enum SupportedNamespace {
  BIP122 = "bip122",
  EIP155 = "eip155",
  XRPL = "xrpl",
  SOLANA = "solana",
  TEZOS = "tezos",
  COSMOS = "cosmos",
}
